import { FetchResult } from '@apollo/client'
import clsx from 'clsx'
import React, { FC, PropsWithChildren, useCallback, useEffect } from 'react'

import { Button, ButtonProps } from '@CoreComponents/Buttons/Button'
import { PromptDialog } from '@Domains/Cart/Components/Prompt/Dialog'
import { useAddToCart } from '@Domains/Cart/Hooks/useAddToCart'
import { AddToCartMutation, AddToCartProductInput, LineItem } from '@Generated/graphql'
import { useTranslation } from '@Globals/Translations'

export interface BuyButtonV2Props extends ButtonProps {
  className?: string
  color?: 'primary' | 'black' | ButtonProps['theme']
  disabled?: boolean
  fillWidth?: boolean
  isInStock?: boolean
  milestoneID?: string
  onPromptChange?: (open: boolean) => void
  onSuccess?: (lineItem?: LineItem) => void
  preventDefault?: boolean
  productID: string
  products?: AddToCartProductInput[]
  quantity?: number
  sourceID?: string
  stopPropagation?: boolean
  useCache?: boolean
  variantID?: string
  withoutSuccessPrompt?: boolean
}

export const BuyButtonV2: FC<PropsWithChildren<BuyButtonV2Props>> = ({
  children,
  className,
  color = 'primary',
  disabled,
  fillWidth = true,
  innerSpacing = 'small',
  isInStock = true,
  milestoneID,
  onPromptChange,
  onSuccess,
  preventDefault,
  productID,
  products,
  quantity = 1,
  sourceID,
  stopPropagation,
  useCache = true,
  variantID,
  withoutSuccessPrompt,
}) => {
  const { t } = useTranslation()
  const [
    addToCart, {
      isDisabled,
      isLoading,
      prompts,
      reset,
      variables,
    },
  ] = useAddToCart({ sourceID, useCache, withoutSuccessPrompt })

  useEffect(() => {
    if (onPromptChange) {
      onPromptChange(!!prompts && prompts.length !== 0)
    }
  }, [onPromptChange, prompts])

  const triggerOnSuccess = useCallback((addToCartResult: FetchResult<AddToCartMutation>) => {
    if (onSuccess && addToCartResult?.data?.addToCart?.success) {
      onSuccess()
    }
    return addToCartResult
  }, [onSuccess])

  if (!isInStock) {
    return (
      <Button disabled theme="white">
        {t('cms:SOLD_OUT_LABEL')}
      </Button>
    )
  }

  return (
    <>
      <Button
        className={clsx(className, sourceID)}
        disabled={isDisabled || disabled}
        fillWidth={fillWidth}
        innerSpacing={innerSpacing}
        loading={isLoading}
        onClick={(e) => {
          if (preventDefault) {
            e.preventDefault()
          }
          if (stopPropagation) {
            e.stopPropagation()
          }
          addToCart(products || [
            {
              quantity,
              productID,
              variantID,
              milestoneID,
            },
          ], {
            withoutSuccessPrompt,
          }).then(triggerOnSuccess)
        }}
        theme={color}
      >
        {(children || t('cms:ADD_TO_CART'))}
      </Button>
      {prompts && prompts.length !== 0 && (
        <PromptDialog
          isLoading={isLoading}
          onClose={reset}
          onSubmit={(a, c) => addToCart(a, { ...c, withoutSuccessPrompt }).then(triggerOnSuccess)}
          prompts={prompts}
          promptsOrigin={variables}
        />
      )}
    </>
  )
}
